/* ═════════════════════════════════════════════════════════════════════════════
   SRC/CONSTANTS/JOURNEYSTAGES.JS — fonte única das 11 etapas da jornada
   ─────────────────────────────────────────────────────────────────────────────
   label é o nome exato que o pipeline grava em metrics.name (catálogo
   canônico, ex.: "Comunicação Op."). Antes, a lista de etapas estava copiada
   em vários componentes com grafias diferentes ("Promoção da saúde",
   "Comunicação Operadora") — uma etapa com grafia divergente sumia ou ia
   para o fim da ordenação sem erro nenhum. Quem precisa ordenar, achar ou
   rotular uma etapa usa as funções daqui, que comparam ignorando acento,
   caixa e espaço.
   ═══════════════════════════════════════════════════════════════════════════ */
import { normalizeKey } from "../utils/normalizeString";

export const JOURNEY_STAGES = [
  { key: "vendas", label: "Vendas", indicator: "CSAT", icon: "ShoppingCart" },
  {
    key: "boas_vindas",
    label: "Boas-Vindas",
    indicator: "CSAT",
    icon: "HeartHandshake",
  },
  {
    key: "agend_consultas",
    label: "Agend. Consultas",
    indicator: "CES",
    icon: "CalendarDays",
  },
  {
    key: "consulta",
    label: "Consulta",
    indicator: "CSAT",
    icon: "Stethoscope",
  },
  {
    key: "agend_exames",
    label: "Agend. Exames",
    indicator: "CES",
    icon: "CalendarCheck",
  },
  { key: "exames", label: "Exames", indicator: "CSAT", icon: "FlaskConical" },
  {
    key: "atend_operadora",
    label: "Atend. Operadora",
    short: "Atend. Op.",
    indicator: "CES",
    icon: "Headphones",
  },
  {
    key: "aplicativo",
    label: "Aplicativo",
    indicator: "CES",
    icon: "Smartphone",
  },
  { key: "portal", label: "Portal", indicator: "CES", icon: "Globe" },
  {
    key: "promocao_saude",
    label: "Promoção da Saúde",
    short: "Prom. Saúde",
    indicator: "CSAT",
    icon: "HeartPulse",
  },
  {
    key: "comunicacao_op",
    label: "Comunicação Op.",
    short: "Comunicação",
    indicator: "CSAT",
    icon: "MessageSquare",
  },
];

export const JOURNEY_STAGE_LABELS = JOURNEY_STAGES.map((s) => s.label);

/** Nome curto para eixo de gráfico, indexado pelo label. */
export const JOURNEY_STAGE_SHORT = Object.fromEntries(
  JOURNEY_STAGES.map((s) => [s.label, s.short ?? s.label]),
);

/** Etapa pelo nome, tolerando acento, caixa e espaço; null se não existe. */
export function findJourneyStage(name) {
  const key = normalizeKey(name);
  if (!key) return null;
  return JOURNEY_STAGES.find((s) => normalizeKey(s.label) === key) ?? null;
}

/** Posição da etapa na jornada; nomes desconhecidos vão para o fim. */
export function journeyStageIndex(name) {
  const stage = findJourneyStage(name);
  return stage ? JOURNEY_STAGES.indexOf(stage) : Number.MAX_SAFE_INTEGER;
}

/** Comparador para .sort() de nomes de etapa na ordem da jornada. */
export function compareJourneyStages(a, b) {
  return journeyStageIndex(a) - journeyStageIndex(b);
}

/**
 * Configuração por etapa no formato de JCFG (_shared.jsx), indexada pelo
 * label: tipo e rótulo do indicador vêm daqui; meta e sys de referência
 * vêm de refByKey (indexado pela key da etapa).
 */
export function buildJourneyConfig(refByKey = {}) {
  return Object.fromEntries(
    JOURNEY_STAGES.map((s) => [
      s.label,
      {
        tipo: s.indicator.toLowerCase(),
        label: s.indicator,
        ...(refByKey[s.key] || {}),
      },
    ]),
  );
}
